import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, TrendingUp, TrendingDown } from "lucide-react";
import Layout from "../component/Layout";
import {
  fetchAllStateRates,
  formatCurrencyINR,
  METALS,
  RATE_FIELDS,
} from "../services/stateRates";
import "./LiveRates.css";

const POLL_MS = 5000;
const ONE_MINUTE_MS = 60 * 1000;
const BASELINE_KEY = "operatorBaselineRates_v3";

const getStorage = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.warn("[LiveRates] Storage error:", e);
  }
};

// Build a flat lookup: map[stateCode][metalKey] = { RTGS_SALE_RATE, RTGS_PURCHASE_RATE }
const buildRateMap = (data) => {
  const map = {};
  data.forEach((state) => {
    map[state.stateCode] = {};
    METALS.forEach((metal) => {
      map[state.stateCode][metal.key] = {
        RTGS_SALE_RATE: state.metals[metal.key]?.RTGS_SALE_RATE,
        RTGS_PURCHASE_RATE: state.metals[metal.key]?.RTGS_PURCHASE_RATE,
      };
    });
  });
  return map;
};

const calculateChange = (current, previous) => {
  if (current == null) return null;
  const curr = Number(current);
  const prev = (previous != null && Number.isFinite(Number(previous))) ? Number(previous) : curr;
  if (!Number.isFinite(curr) || !Number.isFinite(prev) || prev <= 0) return null;

  const change = Number((curr - prev).toFixed(2));
  if (Math.abs(change) < 0.001) {
    return {
      amount: 0,
      percentage: "0.00",
      direction: "flat",
    };
  }

  return {
    amount: change,
    percentage: ((change / prev) * 100).toFixed(2),
    direction: change > 0 ? "up" : "down",
  };
};

const ChangeIndicator = ({ current, previous }) => {
  const prev = (previous != null && Number.isFinite(Number(previous))) ? Number(previous) : null;
  const change = prev != null ? calculateChange(current, prev) : null;

  const isNegative = change?.direction === "down";
  const Icon = isNegative ? TrendingDown : TrendingUp;
  const colorClass = isNegative ? "negative" : "positive";

  const amountStr = change && isNegative
    ? `-${formatCurrencyINR(Math.abs(change.amount))}`
    : `+${formatCurrencyINR(Math.abs(change?.amount || 0))}`;

  const pctStr = change && isNegative
    ? `(-${change.percentage}%)`
    : `(+${change?.percentage || "0.00"}%)`;

  return (
    <div className="rate-change-row">
      <span className={`rate-change-amount ${colorClass}`}>{amountStr}</span>
      <span className={`rate-change-pill ${colorClass}`}>
        <Icon size={14} className="rate-change-icon" />
        <span>{pctStr}</span>
      </span>
    </div>
  );
};

const RateCell = ({ current, previous }) => {
  return (
    <div className="rate-cell-display">
      <div className="rate-main-value">
        {formatCurrencyINR(current)}
        <span className="rate-unit">/g</span>
      </div>
      <ChangeIndicator current={current} previous={previous} />
    </div>
  );
};

const LiveRates = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMetals, setSelectedMetals] = useState(new Set(["gold"]));
  const [search, setSearch] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [previousSnapshotMap, setPreviousSnapshotMap] = useState(null);

  // Holds rates from the LAST poll cycle — used for cycle-to-cycle diff
  const prevRateMapRef = useRef(null);
  // Holds the 1-minute rolling baseline timestamp
  const baselineTimestampRef = useRef(null);
  // Holds the rolling history of live updates + state changes
  const rateHistoryRef = useRef([]);
  const pollCountRef = useRef(0);

  const loadRates = useCallback(async () => {
    try {
      setError("");
      const data = await fetchAllStateRates();
      const now = Date.now();
      pollCountRef.current += 1;

      // Build current rate map from fresh API data
      const currentMap = buildRateMap(data);
      const prevMap = prevRateMapRef.current;

      // --- Collect state changes compared to previous cycle ---
      const stateChanges = [];
      data.forEach((state) => {
        const stateCode = state.stateCode;
        METALS.forEach((metal) => {
          const metalKey = metal.key;
          const currentSale = state.metals[metalKey]?.RTGS_SALE_RATE;
          const prevSale = prevMap?.[stateCode]?.[metalKey]?.RTGS_SALE_RATE;
          const currentPurchase = state.metals[metalKey]?.RTGS_PURCHASE_RATE;
          const prevPurchase = prevMap?.[stateCode]?.[metalKey]?.RTGS_PURCHASE_RATE;
          const adj = state.metals[metalKey]?.ADJUSTMENT;

          const saleDiff = prevSale != null ? Number((currentSale - prevSale).toFixed(2)) : 0;
          const purchaseDiff = prevPurchase != null ? Number((currentPurchase - prevPurchase).toFixed(2)) : 0;

          if (saleDiff !== 0 || purchaseDiff !== 0 || !prevMap) {
            stateChanges.push({
              State: `${state.stateName} (${stateCode})`,
              Metal: metal.label,
              "Sale (₹/g)": currentSale,
              "Sale Diff": saleDiff !== 0 ? `${saleDiff > 0 ? "+" : ""}₹${saleDiff}` : "—",
              "Purchase (₹/g)": currentPurchase,
              "Purchase Diff": purchaseDiff !== 0 ? `${purchaseDiff > 0 ? "+" : ""}₹${purchaseDiff}` : "—",
              "State Markup": adj?.rtgsSale ? `${adj.rtgsSale > 0 ? "+" : ""}${adj.rtgsSale}%` : "0%",
            });
          }
        });
      });

      // Sample base rates from the first state
      const sampleState = data[0];
      const baseGold = sampleState?.metals?.gold?.INDIA_RTGS_SALE;
      const baseSilver = sampleState?.metals?.silver?.INDIA_RTGS_SALE;
      const basePlatinum = sampleState?.metals?.platinum?.INDIA_RTGS_SALE;
      const timeStr = new Date().toLocaleTimeString("en-IN");

      // Save into rolling history (up to last 50 updates)
      const historyEntry = {
        id: pollCountRef.current,
        timestamp: new Date().toISOString(),
        time: timeStr,
        baseRates: { gold: baseGold, silver: baseSilver, platinum: basePlatinum },
        stateChangesCount: prevMap ? stateChanges.length : 0,
        changes: stateChanges,
      };
      rateHistoryRef.current = [historyEntry, ...rateHistoryRef.current.slice(0, 49)];
      if (typeof window !== "undefined") {
        window.__RATE_HISTORY__ = rateHistoryRef.current;
      }

      // ─── Print history of live rate update + state changes ────────────────
      console.group(
        `%c[Live Rate #${pollCountRef.current}] ${timeStr} | Gold: ₹${baseGold}/g | Silver: ₹${baseSilver}/g`,
        "color: #10b981; font-weight: bold;"
      );
      console.log(`Live Base Rates: Gold ₹${baseGold}/g, Silver ₹${baseSilver}/g, Platinum ₹${basePlatinum || "N/A"}/g`);

      if (prevMap && stateChanges.length > 0) {
        console.log(`%c⚠️ ${stateChanges.length} State Rate Changes Detected:`, "color: #f59e0b; font-weight: bold;");
        console.table(stateChanges.slice(0, 10)); // Show top 10 changes in a table
      } else if (!prevMap) {
        console.log(`Initial snapshot loaded for ${data.length} states.`);
      } else {
        console.log("No price change this cycle (market steady).");
      }
      console.groupEnd();
      // ─────────────────────────────────────────────────────────────────────

      // --- Determine previousMap for rendering ---
      let previousMap = null;
      if (prevRateMapRef.current) {
        previousMap = prevRateMapRef.current;
      } else {
        const stored = getStorage(BASELINE_KEY);
        if (stored?.rates) {
          previousMap = stored.rates;
        }
      }

      // Update 1-minute rolling baseline
      const storedBaseline = getStorage(BASELINE_KEY);
      if (!storedBaseline || now - (storedBaseline.timestamp || 0) >= ONE_MINUTE_MS) {
        setStorage(BASELINE_KEY, { timestamp: now, rates: currentMap });
        baselineTimestampRef.current = now;
      }

      // Save current as previous for the NEXT cycle
      prevRateMapRef.current = currentMap;

      setPreviousSnapshotMap(previousMap);
      setRows(data);
      setUpdatedAt(new Date());
    } catch (err) {
      console.error("[LiveRates] Failed to fetch state rates:", err);
      setError("Failed to fetch live state rates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRates();
    const id = setInterval(loadRates, POLL_MS);
    return () => clearInterval(id);
  }, [loadRates]);

  const toggleMetal = (metalKey) => {
    // Only allow one metal at a time
    setSelectedMetals(new Set([metalKey]));
  };

  const tableRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Show all states with selected metal(s)
    const filteredStates = rows.filter((state) => {
      if (!q) return true;
      return (
        state.stateName.toLowerCase().includes(q) ||
        state.stateCode.toLowerCase().includes(q)
      );
    });

    // For each state, create rows for selected metals
    return filteredStates.flatMap((state) =>
      Array.from(selectedMetals).map((metalKey) => {
        const metal = METALS.find((m) => m.key === metalKey);
        // processSnapshots returns: previousMap[stateCode][metalKey] = { RTGS_SALE_RATE, RTGS_PURCHASE_RATE }
        const prevSnapshot = previousSnapshotMap?.[state.stateCode]?.[metalKey];
        return {
          stateName: state.stateName,
          stateCode: state.stateCode,
          metal,
          rates: state.metals[metalKey],
          previousRates: prevSnapshot,
        };
      }),
    );
  }, [selectedMetals, rows, search, previousSnapshotMap]);

  const metalGroups = useMemo(() => {
    const groups = {};
    METALS.forEach((metal) => {
      // Show count of all states with this metal selected
      groups[metal.key] = {
        metal,
        count: selectedMetals.has(metal.key) ? rows.length : 0,
      };
    });
    return groups;
  }, [rows, selectedMetals]);

  return (
    <Layout pageTitle="Live Rates">
      <div className="op-live-rates">
        {/* Header Section */}
        <div className="op-live-rates__header">
          <div className="op-live-rates__title-section">
            <h2 className="op-live-rates__title">Live Metal Rates by State</h2>
            <p className="op-live-rates__subtitle">India API base rates + state constraints</p>
          </div>
          <div className="op-live-rates__status">
            <span className="op-live-rates__badge">● Live</span>
            <span className="op-live-rates__timestamp">
              {updatedAt
                ? `Updated ${updatedAt.toLocaleTimeString("en-IN", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}`
                : "Loading..."}
            </span>
          </div>
        </div>

        {/* Controls Section */}
        <div className="op-live-rates__controls">
          <div className="op-live-rates__metal-filter">
            <span className="op-live-rates__filter-label">Select Metals:</span>
            <div className="op-live-rates__chips">
              {METALS.map((metal) => (
                <button
                  key={metal.key}
                  className={`op-live-rates__chip ${
                    selectedMetals.has(metal.key) ? "op-live-rates__chip--active" : ""
                  }`}
                  onClick={() => toggleMetal(metal.key)}
                >
                  <span className="op-chip-label">{metal.label}</span>
                  <span className="op-chip-count">{metalGroups[metal.key]?.count || 0}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="op-live-rates__search-refresh">
            <input
              className="op-live-rates__search"
              placeholder="Search state, code..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <button className="op-live-rates__refresh" onClick={loadRates} disabled={loading}>
              <RefreshCw size={16} className={loading ? "op-live-rates__spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && <div className="op-live-rates__error">{error}</div>}

        {/* Table Section */}
        <div className="op-live-rates__table-wrap">
          <table className="op-live-rates__table">
            <thead>
              <tr>
                <th className="op-col-state">State</th>
                <th className="op-col-rate">
                  <span className="op-rate-label">RTGS Sale</span>
                  <span className="op-rate-hint">/gram</span>
                </th>
                <th className="op-col-rate">
                  <span className="op-rate-label">RTGS Purchase</span>
                  <span className="op-rate-hint">/gram</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && tableRows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="op-live-rates__empty">
                    <div className="op-empty-content">
                      <div className="op-spinner"></div>
                      <span>Loading live rates...</span>
                    </div>
                  </td>
                </tr>
              ) : tableRows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="op-live-rates__empty">
                    <span>No matching state rates.</span>
                  </td>
                </tr>
              ) : (
                tableRows.map((row) => (
                  <tr key={`${row.stateCode}-${row.metal.key}`} className="op-table-row">
                    <td className="op-col-state">
                      <div className="op-state-cell">
                        <div className="op-state-name">{row.stateName}</div>
                        <div className="op-state-code">{row.stateCode}</div>
                      </div>
                    </td>
                    <td className="op-col-rate">
                      <RateCell
                        current={row.rates?.RTGS_SALE_RATE}
                        previous={row.previousRates?.RTGS_SALE_RATE}
                      />
                    </td>
                    <td className="op-col-rate">
                      <RateCell
                        current={row.rates?.RTGS_PURCHASE_RATE}
                        previous={row.previousRates?.RTGS_PURCHASE_RATE}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Footer */}
        {!loading && tableRows.length > 0 && (
          <div className="op-live-rates__summary">
            <div className="op-summary-item">
              <span className="op-summary-label">Total States:</span>
              <span className="op-summary-value">{new Set(tableRows.map((r) => r.stateCode)).size}</span>
            </div>
            <div className="op-summary-item">
              <span className="op-summary-label">Total Rates Displayed:</span>
              <span className="op-summary-value">{tableRows.length}</span>
            </div>
            <div className="op-summary-item">
              <span className="op-summary-label">Selected Metals:</span>
              <span className="op-summary-value">{Array.from(selectedMetals).join(", ")}</span>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LiveRates;
